import type {Moment} from 'moment-timezone'

export enum OptimizerRunStatus {
  InProgress = 'IN_PROGRESS',
  Success = 'SUCCESS',
  Error = 'ERROR',
  Cancelled = 'CANCELED'
}

export enum OptimizerForecastType {
  UploadedForecast = 'uploaded_forecast',
  StatisticalForecast = 'ds_forecast'
}

export interface OptimizerRunResponse {
  name: string
  forecast_type: OptimizerForecastType.StatisticalForecast
  status: OptimizerRunStatus
  last_updated_at: string
  queued_at: string
  queued_by: string
  optimizer_run_id: number
}

export interface OptimizerRun {
  name: string
  forecastType: OptimizerForecastType.StatisticalForecast
  status: OptimizerRunStatus
  lastUpdatedAt: string
  queuedAt: string
  queuedBy: string
  optimizerRunId: number
}

export enum OptimizerScheduleType {
  Manual = 'manual',
  Optimized = 'optimized',
  Calculating = 'calculating'
}

export enum PlannerLSSettingsKeys {
  OptimizerPageSettings = 'optimizer_page_settings',
  OptimizerSliderSettings = 'optimizer_slider_settings',
  PlanningElectricityChartSettings = 'planning_electricity_chart_settings',
  PlanningChartCardSettings = 'planning_chart_card_settings'
}

interface CollapsibleCardSettings {
  isCardOpen: boolean
}

interface ElectricityChartSettings {
  isElectricityChartOpen: boolean
}

interface SliderSettings {
  defaultRange: number
}

export type PlannerLocalStorageSettingsSchema = {
  [PlannerLSSettingsKeys.OptimizerPageSettings]: CollapsibleCardSettings
  [PlannerLSSettingsKeys.OptimizerSliderSettings]: SliderSettings
  [PlannerLSSettingsKeys.PlanningChartCardSettings]: CollapsibleCardSettings
  [PlannerLSSettingsKeys.PlanningElectricityChartSettings]: ElectricityChartSettings
}

export interface OptimizerSettingsDTO {
  // number values from 0 to 1
  switch_cost_priority: number
  silo_slack_priority: number
  // electricity cost priority is not returned in the API
  // should be calculated like this: 1 - switch_cost_priority - silo_slack_priority
  horizon_days: number
  max_horizon_days: number
  expected_durations: Record<string, {min: number; max: number}>
  time_limit: number
  optimize_from: string
}

export interface OptimizerPriorities {
  switchCostPriority: number
  siloSlackPriority: number
  electricityCostPriority: number
}

export interface OptimizerSettings {
  priorities: OptimizerPriorities
  horizonDays: number
  maxHorizonDays: number
  expectedDurations: Record<string, {min: number; max: number}>
  timeLimit: number
  optimizeFrom: Moment
}

export interface RecalculatePlanDTO {
  name: string
  forecast_type: OptimizerForecastType
  settings: OptimizerSettingsDTO
  save_settings: boolean
}

export interface RecalculatePlanData {
  name: string
  forecastType: OptimizerForecastType
  optimizerSettings: OptimizerSettings
  saveSettings: boolean
}

export interface OptimizerConstraintsDTODescription {
  markdown: string
}

export interface OptimizerConstraintsDTO {
  assets: Record<string, OptimizerConstraintsDTODescription>
  operation_modes: Record<string, OptimizerConstraintsDTODescription>
  combined_markdown: string
}

export interface OptimizerConstraints {
  assets: Record<string, OptimizerConstraintsDTODescription>
  operationModes: Record<string, OptimizerConstraintsDTODescription>
  combinedMarkdown: string
}
